/**
 * PAPI Client Service
 * Singleton pattern for managing Polkadot-API connections to People Chains
 * Uses Smoldot light client for trustless, client-side chain queries
 *
 * ARCHITECTURE:
 * - Smoldot runs in a dedicated Web Worker (keeps main thread free)
 * - Supports eager background initialization via requestIdleCallback
 * - Observable status for React integration
 * - All heavy imports (chain specs, descriptors) are dynamic to keep them out of the initial bundle
 * NOTE: People Chains are parachains and require their relay chain to be connected first
 */

import type { PolkadotClient, TypedApi } from 'polkadot-api';
import type { polkadot_people } from '@polkadot-api/descriptors';
import type { ksmcc3_people } from '@polkadot-api/descriptors';
import { setStatus, getPapiStatus } from '@/lib/papi-status.client';

// Type definitions
type PolkadotPeopleApi = TypedApi<typeof polkadot_people>;
type KusamaPeopleApi = TypedApi<typeof ksmcc3_people>;
type SmoldotInstance = Awaited<ReturnType<typeof import('polkadot-api/smoldot').start>>;
type SmoldotChain = Awaited<ReturnType<SmoldotInstance['addChain']>>;

// ==================== SINGLETON STATE ====================

// Singleton state for Smoldot
let smoldotInstance: SmoldotInstance | null = null;
let smoldotInitPromise: Promise<SmoldotInstance> | null = null;

// Relay chain state (required for parachains)
let polkadotRelayChain: SmoldotChain | null = null;
let kusamaRelayChain: SmoldotChain | null = null;

// Lazy-loaded People Chain state
let polkadotPeopleClient: PolkadotClient | null = null;
let polkadotPeopleApi: PolkadotPeopleApi | null = null;
let polkadotPeopleInitPromise: Promise<PolkadotPeopleApi> | null = null;

let kusamaPeopleClient: PolkadotClient | null = null;
let kusamaPeopleApi: KusamaPeopleApi | null = null;
let kusamaPeopleInitPromise: Promise<KusamaPeopleApi> | null = null;

// ==================== SMOLDOT INITIALIZATION ====================

/**
 * Initialize Smoldot if not already running
 * Uses worker for off-main-thread execution when possible
 */
async function getSmoldot(): Promise<SmoldotInstance> {
	if (smoldotInstance) return smoldotInstance;
	if (smoldotInitPromise) return smoldotInitPromise;

	smoldotInitPromise = (async () => {
		// Only run on client side
		if (typeof window === 'undefined') {
			throw new Error('Smoldot can only run in browser environment');
		}

		// Try worker-based approach first (keeps main thread free), fallback to main thread
		try {
			const { startFromWorker } = await import('polkadot-api/smoldot/from-worker');
			// Use Webpack-compatible worker URL pattern (works with Next.js)
			const smWorker = new Worker(new URL('polkadot-api/smoldot/worker', import.meta.url));
			smoldotInstance = startFromWorker(smWorker);
			console.log('[PAPI] Smoldot started in dedicated worker');
		} catch (workerError) {
			console.warn('[PAPI] Worker initialization failed, falling back to main thread:', workerError);
			const { start } = await import('polkadot-api/smoldot');
			smoldotInstance = start();
			console.log('[PAPI] Smoldot started on main thread (fallback)');
		}

		return smoldotInstance;
	})();

	return smoldotInitPromise;
}

// ==================== RELAY CHAIN CONNECTIONS ====================

/**
 * Filter chain spec to only include secure (wss) bootnodes when on HTTPS pages
 * This prevents "Mixed Content" browser errors in production
 */
function getSecureChainSpec(chainSpec: string): string {
	// Only filter on HTTPS pages
	if (typeof window === 'undefined' || window.location.protocol !== 'https:') {
		return chainSpec;
	}

	try {
		const spec = JSON.parse(chainSpec);
		if (spec.bootNodes && Array.isArray(spec.bootNodes)) {
			// Keep only bootnodes with /wss/ in the multiaddr (secure WebSocket)
			spec.bootNodes = spec.bootNodes.filter((node: string) => node.includes('/wss/'));
		}
		return JSON.stringify(spec);
	} catch {
		// If parsing fails, return original
		return chainSpec;
	}
}

/**
 * Get or create the Polkadot relay chain connection
 * Required before connecting to Polkadot People Chain
 */
async function getPolkadotRelayChain(): Promise<SmoldotChain> {
	if (polkadotRelayChain) return polkadotRelayChain;

	const smoldot = await getSmoldot();
	const { polkadot } = await import('polkadot-api/chains');
	polkadotRelayChain = await smoldot.addChain({ chainSpec: getSecureChainSpec(polkadot) });
	console.log('[PAPI] Polkadot relay chain connected');
	return polkadotRelayChain;
}

/**
 * Get or create the Kusama relay chain connection
 * Required before connecting to Kusama People Chain
 */
async function getKusamaRelayChain(): Promise<SmoldotChain> {
	if (kusamaRelayChain) return kusamaRelayChain;

	const smoldot = await getSmoldot();
	const { ksmcc3 } = await import('polkadot-api/chains');
	kusamaRelayChain = await smoldot.addChain({ chainSpec: getSecureChainSpec(ksmcc3) });
	console.log('[PAPI] Kusama relay chain connected');
	return kusamaRelayChain;
}

// ==================== PEOPLE CHAIN CONNECTIONS ====================

/**
 * Initialize Polkadot People Chain connection (lazy, on-demand)
 */
async function initPolkadotPeopleChain(): Promise<PolkadotPeopleApi> {
	if (polkadotPeopleApi) return polkadotPeopleApi;
	if (polkadotPeopleInitPromise) return polkadotPeopleInitPromise;

	polkadotPeopleInitPromise = (async () => {
		try {
			const [{ createClient }, { getSmProvider }, { polkadot_people: polkadotPeopleDescriptor }] = await Promise.all([
				import('polkadot-api'),
				import('polkadot-api/sm-provider'),
				import('@polkadot-api/descriptors')
			]);

			const smoldot = await getSmoldot();
			const relayChain = await getPolkadotRelayChain();

			const { polkadot_people: polkadotPeopleChainSpec } = await import('polkadot-api/chains');

			// Add People Chain as parachain of Polkadot
			const polkadotPeopleChain = await smoldot.addChain({
				chainSpec: getSecureChainSpec(polkadotPeopleChainSpec),
				potentialRelayChains: [relayChain]
			});

			// Create PAPI client
			polkadotPeopleClient = createClient(getSmProvider(polkadotPeopleChain));
			polkadotPeopleApi = polkadotPeopleClient.getTypedApi(polkadotPeopleDescriptor);

			console.log('[PAPI] Polkadot People Chain connected');
			return polkadotPeopleApi;
		} catch (error) {
			console.error('[PAPI] Failed to connect Polkadot People Chain:', error);
			polkadotPeopleInitPromise = null;
			throw error;
		}
	})();

	return polkadotPeopleInitPromise;
}

/**
 * Initialize Kusama People Chain connection (lazy, on-demand)
 * Only called when identity not found on Polkadot
 */
async function initKusamaPeopleChain(): Promise<KusamaPeopleApi> {
	if (kusamaPeopleApi) return kusamaPeopleApi;
	if (kusamaPeopleInitPromise) return kusamaPeopleInitPromise;

	kusamaPeopleInitPromise = (async () => {
		try {
			const [{ createClient }, { getSmProvider }, { ksmcc3_people: kusamaPeopleDescriptor }] = await Promise.all([
				import('polkadot-api'),
				import('polkadot-api/sm-provider'),
				import('@polkadot-api/descriptors')
			]);

			const smoldot = await getSmoldot();
			const relayChain = await getKusamaRelayChain();

			const { ksmcc3_people: kusamaPeopleChainSpec } = await import('polkadot-api/chains');

			// Add People Chain as parachain of Kusama
			const kusamaPeopleChain = await smoldot.addChain({
				chainSpec: getSecureChainSpec(kusamaPeopleChainSpec),
				potentialRelayChains: [relayChain]
			});

			// Create PAPI client
			kusamaPeopleClient = createClient(getSmProvider(kusamaPeopleChain));
			kusamaPeopleApi = kusamaPeopleClient.getTypedApi(kusamaPeopleDescriptor);

			console.log('[PAPI] Kusama People Chain connected');
			return kusamaPeopleApi;
		} catch (error) {
			console.error('[PAPI] Failed to connect Kusama People Chain:', error);
			kusamaPeopleInitPromise = null;
			throw error;
		}
	})();

	return kusamaPeopleInitPromise;
}

// ==================== PUBLIC API ====================

/**
 * Get the Polkadot People Chain typed API (lazy initialization)
 */
export async function getPolkadotPeopleApi(): Promise<PolkadotPeopleApi> {
	return initPolkadotPeopleChain();
}

/**
 * Get the Kusama People Chain typed API (lazy initialization)
 */
export async function getKusamaPeopleApi(): Promise<KusamaPeopleApi> {
	return initKusamaPeopleChain();
}

/**
 * Check if PAPI has any active connections
 */
export function isPapiReady(): boolean {
	return smoldotInstance !== null;
}

/**
 * Pre-initialize PAPI connections in the background
 * Uses requestIdleCallback to run when browser is idle
 *
 * Call this early (e.g., on app mount) to have connections ready before identity requests
 * Only pre-connects Polkadot chain (Kusama is fallback, loaded on-demand)
 */
export function preloadPapiInBackground(): void {
	// Only run on client side
	if (typeof window === 'undefined') return;

	// Don't start if already initializing or ready
	if (getPapiStatus() !== 'idle') return;

	// Use requestIdleCallback if available, otherwise setTimeout
	const scheduleIdle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1));

	scheduleIdle(
		async () => {
			setStatus('initializing');
			console.log('[PAPI] Starting background pre-initialization...');

			try {
				await initPolkadotPeopleChain();
				setStatus('ready');
				console.log('[PAPI] Background pre-initialization complete');
			} catch (error) {
				setStatus('error');
				console.warn('[PAPI] Background pre-initialization failed, will retry on demand:', error);
			}
		},
		{ timeout: 5000 } // Max 5 second wait for idle
	);
}

/**
 * Cleanup function for when the app unmounts
 * Should be called in a top-level cleanup effect
 */
export function destroyPapiClients(): void {
	polkadotPeopleClient?.destroy();
	kusamaPeopleClient?.destroy();
	smoldotInstance?.terminate();

	polkadotPeopleClient = null;
	kusamaPeopleClient = null;
	polkadotPeopleApi = null;
	kusamaPeopleApi = null;
	polkadotRelayChain = null;
	kusamaRelayChain = null;
	smoldotInstance = null;
	polkadotPeopleInitPromise = null;
	kusamaPeopleInitPromise = null;
	smoldotInitPromise = null;
	setStatus('idle');

	console.log('[PAPI] All clients destroyed');
}
